import React from 'react';
import { Grid, InputAdornment } from '@mui/material';
import { DecimalNumberInput, SegmentedControl } from '../ui';
import { APP_CURRENCY } from '../../utils/constants/currency';
import { formatCurrency } from '../../utils/formatters/money';
import { FormField, FormLabel, HelperText } from './ProductDrawer/ProductDrawer.styles';
import { CostEntryMode, derivePotentialEconomics } from './productCostPricing.utils';

const COST_ENTRY_OPTIONS = [
  { value: 'PER_PIECE' as const, label: 'Por pieza', ariaLabel: 'costo por pieza' },
  { value: 'PER_GRAM' as const, label: 'Por gramo', ariaLabel: 'costo por gramo' },
];

export interface ProductCostEntryFieldsProps {
  costEntryMode: CostEntryMode;
  costPerGram: string;
  costPrice: string;
  sellingPrice: string;
  hasWeight: boolean;
  onCostEntryModeChange: (next: CostEntryMode) => void;
  onCostPriceChange: (val: number | null) => void;
  onCostPerGramChange: (val: number | null) => void;
  onSellingPriceChange: (val: number | null) => void;
}

export const ProductCostEntryFields: React.FC<ProductCostEntryFieldsProps> = ({
  costEntryMode,
  costPerGram,
  costPrice,
  sellingPrice,
  hasWeight,
  onCostEntryModeChange,
  onCostPriceChange,
  onCostPerGramChange,
  onSellingPriceChange,
}) => {
  const economics = derivePotentialEconomics(costPrice, sellingPrice);
  const gramDisabled = costEntryMode === 'PER_PIECE' || !hasWeight;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormField>
          <FormLabel>Cómo cargar el costo</FormLabel>
          <SegmentedControl
            value={costEntryMode}
            options={COST_ENTRY_OPTIONS}
            onChange={onCostEntryModeChange}
            ariaLabel="modo de costo"
            fullWidth
            size="small"
          />
          <HelperText>Al cambiar de opción se reinician los costos para cargar de cero.</HelperText>
        </FormField>
      </Grid>
      <Grid item xs={12}>
        <FormField>
          <FormLabel>Costo por pieza{costEntryMode === 'PER_PIECE' ? ' *' : ''}</FormLabel>
          <DecimalNumberInput
            fullWidth
            name="costPrice"
            value={costPrice}
            onChange={onCostPriceChange}
            variant="outlined"
            size="small"
            placeholder="0,00"
            decimalPlaces={2}
            min={0.01}
            max={999999.99}
            disabled={costEntryMode === 'PER_GRAM'}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">{APP_CURRENCY.symbol}</InputAdornment>
              ),
            }}
            required={costEntryMode === 'PER_PIECE'}
          />
          {costEntryMode === 'PER_GRAM' ? (
            <HelperText>Calculado automáticamente: peso × costo por gramo</HelperText>
          ) : (
            <HelperText color="warning.main">Solo se permiten hasta 2 decimales</HelperText>
          )}
        </FormField>
      </Grid>
      <Grid item xs={12}>
        <FormField>
          <FormLabel>Costo por gramo{costEntryMode === 'PER_GRAM' ? ' *' : ''}</FormLabel>
          <DecimalNumberInput
            fullWidth
            name="costPerGram"
            value={costPerGram}
            onChange={onCostPerGramChange}
            variant="outlined"
            size="small"
            placeholder="0,00"
            decimalPlaces={2}
            min={0.01}
            max={999999.99}
            disabled={gramDisabled}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">{APP_CURRENCY.symbol}</InputAdornment>
              ),
            }}
            required={costEntryMode === 'PER_GRAM'}
          />
          {costEntryMode === 'PER_PIECE' ? (
            !hasWeight ? (
              <HelperText>Definí el peso en Propiedades para ver el costo por gramo</HelperText>
            ) : null
          ) : !hasWeight ? (
            <HelperText color="warning.main">
              Definí el peso en Propiedades para calcular el costo por pieza
            </HelperText>
          ) : (
            <HelperText color="warning.main">Solo se permiten hasta 2 decimales</HelperText>
          )}
        </FormField>
      </Grid>
      <Grid item xs={12}>
        <FormField>
          <FormLabel>Precio de Venta</FormLabel>
          <DecimalNumberInput
            fullWidth
            name="sellingPrice"
            value={sellingPrice}
            onChange={onSellingPriceChange}
            variant="outlined"
            size="small"
            placeholder="0,00"
            decimalPlaces={2}
            min={0}
            max={999999.99}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">{APP_CURRENCY.symbol}</InputAdornment>
              ),
            }}
          />
          {economics != null ? (
            <HelperText>
              Utilidad potencial: {formatCurrency(economics.profit)} · Margen potencial:{' '}
              {economics.marginPercent}%
            </HelperText>
          ) : (
            <HelperText color="warning.main">Solo se permiten hasta 2 decimales</HelperText>
          )}
        </FormField>
      </Grid>
    </Grid>
  );
};
